import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { outreachApi } from './api/outreachApi';

// One number for the sidebar badge and the Unibox tab, so reading a thread
// on the page drops the count in the menu without a round trip.
const UnreadContext = createContext({
  unread: 0,
  overdue: 0,
  loaded: true,
  setUnread: () => {},
  refreshUnread: () => {},
});

const POLL_MS = 2 * 60 * 1000;

export function UnreadProvider({ enabled, children }) {
  const { token } = useAuth();
  const [unread, setUnread] = useState(0);
  const [overdue, setOverdue] = useState(0);
  // false only until the first count resolves, so the badge shows a loader on the
  // initial page load and never flickers to it on the background poll after that.
  const [loaded, setLoaded] = useState(false);

  const refreshUnread = useCallback(async () => {
    if (!enabled || !token) {
      setLoaded(true);
      return;
    }
    try {
      // The list already counts them over the whole mailbox, before any tab filter.
      const data = await outreachApi.listInbox({ limit: 1 });
      setUnread(data.unread_threads);
      setOverdue(data.overdue_threads);
    } catch (e) {
      // On every page of the app: an unreachable service keeps the last count
      // here and is reported properly on the page itself.
      console.warn(`Unread count unavailable: ${e.message}`);
    } finally {
      setLoaded(true);
    }
  }, [enabled, token]);

  useEffect(() => {
    refreshUnread();
    const timer = setInterval(refreshUnread, POLL_MS);
    return () => clearInterval(timer);
  }, [refreshUnread]);

  return (
    <UnreadContext.Provider value={{ unread, overdue, loaded, setUnread, refreshUnread }}>
      {children}
    </UnreadContext.Provider>
  );
}

export const useUnread = () => useContext(UnreadContext);
